import { c as _c } from "react/compiler-runtime";
import { Stack, type SxProps, type Theme } from '@mui/material';
import classnames from 'classnames';
import { forwardRef, type PropsWithChildren } from 'react';
import { PanelFooter, type PanelFooterProps } from '@/components/panels/panelFooter';
import { PanelHeader, type PanelHeaderProps } from '@/components/panels/panelHeader';
import { getTestId } from '@/components/panels/panelTestId';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
export interface PanelWrapperProps {
  testIdPrefix?: string;
  rounded?: boolean;
  header?: PanelHeaderProps;
  footer?: PanelFooterProps;
  sx?: SxProps<Theme>;
}
export const PanelWrapper = forwardRef<HTMLDivElement, PropsWithChildren<PanelWrapperProps>>((props, ref) => {
  const $ = _c(28);
  const {
    testIdPrefix,
    children,
    footer,
    header,
    rounded
  } = props;
  let t0;
  if ($[0] !== testIdPrefix) {
    t0 = getTestId(testIdPrefix, "root");
    $[0] = testIdPrefix;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      justifyContent: "space-between"
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.sx) {
    t2 = Array.isArray(props.sx) ? props.sx : [props.sx ?? false];
    $[3] = props.sx;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = [t1, ...t2];
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== header) {
    t4 = header && <ScrollableHeader hideBorderWhenOnTop={true}><PanelHeader {...header} /></ScrollableHeader>;
    $[7] = header;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== rounded) {
    t5 = classnames("h-full", {
      "rounded-b-[16px]": rounded
    });
    $[9] = rounded;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== testIdPrefix) {
    t6 = getTestId(testIdPrefix, "panel-body");
    $[11] = testIdPrefix;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== children || $[14] !== t5 || $[15] !== t6) {
    t7 = <ScrollableContent className={t5} data-testid={t6}>{children}</ScrollableContent>;
    $[13] = children;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t7) {
    t8 = <ScrollableWrapper><Stack className="flex-1 min-h-0">{t4}{t7}</Stack></ScrollableWrapper>;
    $[17] = t4;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== footer) {
    t9 = footer && <PanelFooter {...footer} />;
    $[20] = footer;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== ref || $[23] !== t0 || $[24] !== t3 || $[25] !== t8 || $[26] !== t9) {
    t10 = <Stack data-testid={t0} ref={ref} className="h-full flex-1" sx={t3}>{t8}{t9}</Stack>;
    $[22] = ref;
    $[23] = t0;
    $[24] = t3;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
});